import { Web } from 'sip.js'
import { causeFromSipResponse, isDialableSipUser } from './helpers'

const DISPOSE_TIMEOUT_MS = 3000

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise(resolve => setTimeout(resolve, ms)),
  ])
}

// Never rejects and never takes longer than DISPOSE_TIMEOUT_MS.
async function stopSimpleUser(simpleUser) {
  try {
    await withTimeout(simpleUser.disconnect(), DISPOSE_TIMEOUT_MS)
  } catch (err) {}
}

// Wrapper around SIP.js SimpleUser for outbound calls only. It reports plain
// events through one listener so the saga can wrap them in an eventChannel:
//   registered | unregistered | register-rejected {statusCode}
//   server-disconnect | call-answered | call-hangup {cause}
//
// Incoming calls: no onCallReceived delegate is set, so SimpleUser rejects
// every incoming INVITE itself (480) and never keeps it as its single
// session, which would otherwise make the next call() fail with
// "Session already exists". Session events are only reported while one of
// our own outbound calls is in progress, so that rejection stays silent.
//
// Reconnection is owned by the saga: on transport loss this client stops
// SimpleUser's own reconnect attempts and reports server-disconnect, and the
// saga calls disconnect() and, after a backoff, connect() again.
//
// One SimpleUser is built per client and reused by every connect(). sip.js
// 0.20.1's SimpleUser constructor adds a window 'online' listener that it
// never removes, so each instance built stays in memory for the life of the
// page; building one per reconnect would leak one per retry.
export default class SipClient {
  constructor({
    wsUrl,
    ext,
    password,
    realm,
    iceServers,
    iceGatheringTimeout,
    audioElement,
  }) {
    this.wsUrl = wsUrl
    this.ext = ext
    this.password = password
    // The realm comes from /sip-credentials, never from env: the gateway bakes
    // that exact string into the digest FreeSWITCH verifies.
    this.realm = realm
    this.iceServers = iceServers
    this.iceGatheringTimeout = iceGatheringTimeout
    this.audioElement = audioElement
    this.listener = null
    this.simpleUser = null
    this.disposed = false
    this.outbound = false
    this.pendingCause = null
    this.localHangup = false
  }

  setListener(listener) {
    this.listener = listener
  }

  emit(event) {
    if (this.listener && !this.disposed) this.listener(event)
  }

  buildOptions() {
    const sdhOptions = { iceGatheringTimeout: this.iceGatheringTimeout }
    if (this.iceServers) {
      sdhOptions.peerConnectionConfiguration = { iceServers: this.iceServers }
    }
    return {
      aor: `sip:${this.ext}@${this.realm}`,
      delegate: {
        onCallAnswered: () => {
          if (this.outbound) this.emit({ type: 'call-answered' })
        },
        onCallHangup: () => {
          if (!this.outbound) return
          this.outbound = false
          // The INVITE's onReject (which carries the status code) runs right
          // after this callback, so read the cause one microtask later.
          Promise.resolve().then(() => {
            this.emit({
              type: 'call-hangup',
              cause: this.localHangup ? null : this.pendingCause,
            })
          })
        },
        onRegistered: () => this.emit({ type: 'registered' }),
        onUnregistered: () => this.emit({ type: 'unregistered' }),
        onServerDisconnect: () => {
          if (this.disposed) return
          this.emit({ type: 'server-disconnect' })
          // SimpleUser schedules its own reconnect right after this callback
          // and checks again before running it; disconnecting first cancels it.
          Promise.resolve().then(() => this.stopTransport())
        },
      },
      media: {
        constraints: { audio: true, video: false },
        remote: this.audioElement ? { audio: this.audioElement } : undefined,
      },
      userAgentOptions: {
        authorizationUsername: this.ext,
        authorizationPassword: this.password,
        logLevel: 'warn',
        sessionDescriptionHandlerFactoryOptions: sdhOptions,
      },
    }
  }

  // Connects the transport and sends REGISTER. Resolves once REGISTER is
  // sent; the outcome arrives as registered / register-rejected. May be
  // called again after disconnect().
  async connect() {
    if (this.disposed) throw new Error('SIP client disposed')
    this.outbound = false
    this.pendingCause = null
    this.localHangup = false
    if (!this.simpleUser) {
      this.simpleUser = new Web.SimpleUser(this.wsUrl, this.buildOptions())
    }
    await this.simpleUser.connect()
    await this.simpleUser.register(undefined, {
      requestDelegate: {
        onReject: response => {
          this.emit({
            type: 'register-rejected',
            statusCode:
              response && response.message ? response.message.statusCode : null,
          })
        },
      },
    })
  }

  // Sends INVITE to sip:<sip user>@<realm>. Resolves once the INVITE is sent;
  // rejects when it cannot be sent (no microphone, existing session). The
  // target is checked here too, since it goes straight into the URI: anything
  // outside the dialable set (an admin ext, '@', ':' ...) is never sent.
  async call(target) {
    if (!this.simpleUser) throw new Error('SIP not connected')
    if (!isDialableSipUser(target)) throw new Error('SIP target not dialable')
    this.outbound = true
    this.pendingCause = null
    this.localHangup = false
    try {
      await this.simpleUser.call(`sip:${target}@${this.realm}`, undefined, {
        requestDelegate: {
          onReject: response => {
            const message = response && response.message
            this.pendingCause = message
              ? causeFromSipResponse(
                  message.statusCode,
                  message.getHeader('reason'),
                )
              : 'failed'
          },
        },
      })
    } catch (err) {
      this.outbound = false
      throw err
    }
  }

  // CANCEL while ringing, BYE once answered. Rejects when there is no call.
  async hangup() {
    if (!this.simpleUser) throw new Error('SIP not connected')
    this.localHangup = true
    await this.simpleUser.hangup()
  }

  setMute(muted) {
    if (!this.simpleUser) throw new Error('SIP not connected')
    if (muted) this.simpleUser.mute()
    else this.simpleUser.unmute()
    return this.simpleUser.isMuted() === muted
  }

  hasActiveCall() {
    return this.outbound
  }

  stopTransport() {
    if (!this.simpleUser) return
    try {
      this.simpleUser.disconnect().catch(() => {})
    } catch (err) {}
  }

  // Unregisters, ends any call and closes the transport, but keeps the
  // SimpleUser for the next connect(). Never rejects and never takes longer
  // than DISPOSE_TIMEOUT_MS.
  async disconnect() {
    this.outbound = false
    if (this.simpleUser) await stopSimpleUser(this.simpleUser)
  }

  // Like disconnect(), but final: the client goes quiet and cannot connect
  // again.
  async dispose() {
    if (this.disposed) return
    this.disposed = true
    this.listener = null
    this.outbound = false
    const simpleUser = this.simpleUser
    this.simpleUser = null
    if (simpleUser) await stopSimpleUser(simpleUser)
  }
}
